<template>
  <div
    class="flex items-center w-full h-11 rounded-lg border bg-white dark:bg-slate-900 text-[16px] transition focus-within:ring-2 focus-within:ring-red-500/30"
    :class="error
      ? 'border-red-400 focus-within:border-red-500'
      : 'border-slate-200 dark:border-slate-700 focus-within:border-red-400'"
  >
    <!-- Fixed, non-editable international prefix. Part of the value, so hidden
         from screen readers (the digits below carry it). -->
    <span aria-hidden="true" class="pl-4 pr-1 text-slate-500 dark:text-slate-400 select-none">+</span>
    <input
      :id="id"
      :value="digits"
      type="tel"
      inputmode="numeric"
      autocomplete="tel"
      :placeholder="placeholder || undefined"
      :aria-label="ariaLabel || undefined"
      :aria-invalid="error || undefined"
      class="flex-1 min-w-0 h-full bg-transparent pr-4 outline-none"
      @input="onInput"
      @blur="emit('blur')"
    >
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { digitsOnly, toIntlDigits } from '~/utils/phone';

// modelValue is the full submitted value ("+" + digits, or ''). The inner input
// only ever shows/accepts digits; the '+' is a visual prefix. Any letters,
// separators or stray '+' (incl. a pasted "+7 903…") are stripped on input.
const props = withDefaults(
  defineProps<{
    modelValue: string;
    error?: boolean;
    placeholder?: string;
    ariaLabel?: string;
    id?: string;
  }>(),
  { error: false, placeholder: '', ariaLabel: '', id: undefined },
);

const emit = defineEmits<{
  'update:modelValue': [value: string];
  blur: [];
}>();

const digits = computed(() => digitsOnly(props.modelValue));

function onInput(e: Event) {
  const el = e.target as HTMLInputElement;
  const cleaned = digitsOnly(el.value);
  // Force the visible field back to digits so a typed letter / pasted symbol
  // never sticks, even if the emitted value happens to equal the current one
  // (Vue won't re-patch an unchanged :value otherwise).
  if (el.value !== cleaned) el.value = cleaned;
  emit('update:modelValue', toIntlDigits(cleaned));
}
</script>
